import { useCallback, useEffect, useMemo, useState } from "react";
import { ToolStep } from "./toolSteps";

/**
 * Archi's conversations: several at once, like Linear's, each a tab in the
 * dock. A conversation keeps the lens it was started in — asking in it later
 * reads the same tenants, whatever the side menu shows by then.
 *
 * Kept in this browser, per signed-in user, so they survive a reload. The
 * durable home is the ArchiConversations / ArchiMessages tables behind
 * `apps/archi` (P-CTX-123); until then the server keeps each one's history
 * in memory, and after it restarts a conversation continues with its
 * transcript on screen but without the model remembering earlier turns.
 */

export interface Turn {
  id: string;
  role: "user" | "assistant";
  text: string;
  steps: ToolStep[];
  streaming: boolean;
  /** A read ran since the last text: the text that follows starts a new paragraph. */
  afterTool?: boolean;
  error?: string;
  /** The organization spent this month's allowance: a state, not a failure. */
  usageLimit?: { message: string; resetsOn: string | null };
}

export interface Session {
  id: string;
  title: string;
  /** Renamed by the user: the first question no longer names it. */
  renamed: boolean;
  /** The conversation's id on the Archi service, once it answered once. */
  serverId: string | null;
  /** The lens it was started in: null for everything the user can see. */
  lensTenantId: number | null;
  lensLabel: string;
  turns: Turn[];
  updatedAt: number;
  /** Closed: out of the dock, still in History until deleted. */
  closed?: boolean;
}

export interface Lens {
  tenantId: number | null;
  label: string;
}

/** More than anyone keeps open; the least recently used goes first. */
const MAX_SESSIONS = 20;
const TITLE_LENGTH = 48;

const storageKey = (user: string) => `archi.sessions.v1:${user}`;

let seq = 0;
export const newId = (prefix: string) =>
  `${prefix}${Date.now().toString(36)}${(++seq).toString(36)}`;

/** A question, as a tab's title: its first line, cut at a word. */
export function titleOf(question: string): string {
  const line = question.trim().split("\n")[0] ?? "";
  if (line.length <= TITLE_LENGTH) return line;
  const cut = line.slice(0, TITLE_LENGTH);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 24))}…`;
}

function load(user: string): Session[] {
  try {
    const raw = window.localStorage.getItem(storageKey(user));
    const sessions = raw ? (JSON.parse(raw) as Session[]) : [];
    // At most one blank conversation: any others carry nothing worth a tab.
    const firstBlank = sessions.find((s) => s.turns.length === 0);
    const kept = sessions.filter((s) => s.turns.length > 0 || s === firstBlank);
    // An answer cut off by a reload is over: it is not still streaming.
    return kept.map((s) => ({
      ...s,
      turns: s.turns.map((t) =>
        t.streaming
          ? { ...t, streaming: false, error: t.error ?? "Interrupted." }
          : t,
      ),
    }));
  } catch {
    return [];
  }
}

function save(user: string, sessions: Session[]): void {
  try {
    window.localStorage.setItem(storageKey(user), JSON.stringify(sessions));
  } catch {
    // Private mode or a full quota: the conversations still work, unsaved.
  }
}

/** The list and whose it is, as one value: it is only ever saved under its owner. */
interface Owned {
  user: string;
  sessions: Session[];
}

export function useArchiSessions(user: string) {
  const [state, setState] = useState<Owned>(() => ({
    user,
    sessions: load(user),
  }));
  const [activeId, setActiveId] = useState<string | null>(null);

  // The signed-in user settles after the first render (Auth0 restores the
  // session), and another user may sign in on this browser: each gets their
  // own list. Owner and list change together, so the list one user had is
  // never written over another's.
  useEffect(() => {
    if (state.user === user) return;
    setState({ user, sessions: load(user) });
    setActiveId(null);
  }, [user, state.user]);

  useEffect(() => {
    if (state.user === user) save(state.user, state.sessions);
  }, [state, user]);

  const setSessions = useCallback((change: (all: Session[]) => Session[]) => {
    setState((current) => ({ ...current, sessions: change(current.sessions) }));
  }, []);
  const sessions = state.sessions;

  /**
   * A new conversation — unless a blank one is already there, which opens
   * instead: however often "+" is pressed, there is at most one empty chat.
   */
  const create = useCallback(
    (lens: Lens): string => {
      const blank = sessions.find((s) => s.turns.length === 0);
      if (blank) {
        setSessions((all) =>
          all.map((s) =>
            s.id === blank.id
              ? {
                  ...s,
                  closed: false,
                  lensTenantId: lens.tenantId,
                  lensLabel: lens.label,
                  updatedAt: Date.now(),
                }
              : s,
          ),
        );
        setActiveId(blank.id);
        return blank.id;
      }
      const id = newId("s");
      setSessions((all) =>
        [
          {
            id,
            title: "New chat",
            renamed: false,
            serverId: null,
            lensTenantId: lens.tenantId,
            lensLabel: lens.label,
            turns: [],
            updatedAt: Date.now(),
          },
          ...all,
        ].slice(0, MAX_SESSIONS),
      );
      setActiveId(id);
      return id;
    },
    [sessions, setSessions],
  );

  /** Applies `change` to one conversation; a no-op once it is deleted. */
  const update = useCallback(
    (id: string, change: (s: Session) => Session) => {
      setSessions((all) =>
        all.map((s) =>
          s.id === id ? { ...change(s), updatedAt: Date.now() } : s,
        ),
      );
    },
    [setSessions],
  );

  const rename = useCallback(
    (id: string, title: string) => {
      const clean = title.trim();
      if (clean) update(id, (s) => ({ ...s, title: clean, renamed: true }));
    },
    [update],
  );

  /** Out of the dock; kept in History. A blank one has nothing to keep and goes. */
  const close = useCallback(
    (id: string) => {
      setSessions((all) =>
        all.flatMap((s) =>
          s.id !== id
            ? [s]
            : s.turns.length === 0
              ? []
              : [{ ...s, closed: true }],
        ),
      );
      setActiveId((current) => (current === id ? null : current));
    },
    [setSessions],
  );

  /** Back in the dock, and open. */
  const reopen = useCallback(
    (id: string) => {
      setSessions((all) =>
        all.map((s) => (s.id === id ? { ...s, closed: false } : s)),
      );
      setActiveId(id);
    },
    [setSessions],
  );

  /** Gone for good, from this browser. */
  const remove = useCallback(
    (id: string) => {
      setSessions((all) => all.filter((s) => s.id !== id));
      setActiveId((current) => (current === id ? null : current));
    },
    [setSessions],
  );

  const active = useMemo(
    () => sessions.find((s) => s.id === activeId) ?? null,
    [sessions, activeId],
  );

  return {
    sessions,
    active,
    activeId,
    setActiveId,
    create,
    update,
    rename,
    close,
    reopen,
    remove,
  };
}
